import {
  formatDateParam,
  parseEventDateValue,
} from 'shared/helpers/DateHelper';

const ATTACHED_CONTEXT_RE =
  /<attached_context>[\s\S]*?<\/attached_context>\s*/gi;
const ATTACHED_EVENTS_RE = /<attached_events>[\s\S]*?<\/attached_events>\s*/gi;
const LEGACY_EVENT_CONTEXT_RE = /^\[Event Context:[^\]]*\]\s*/i;

const MENTION_TAG_RES = [
  { type: 'contact', re: /<contact\s+id="([^"]*)"\s+name="([^"]*)">/gi },
  {
    type: 'conversation',
    re: /<conversation\s+id="([^"]*)"\s+name="([^"]*)">/gi,
  },
  {
    type: 'article',
    re: /<article\s+id="([^"]*)"\s+name="([^"]*)"(?:\s+portal_slug="([^"]*)")?>/gi,
    portalSlugIndex: 3,
  },
  { type: 'event', re: /<event\s+id="([^"]*)"\s+name="([^"]*)">/gi },
];

function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

function unescapeAttr(value) {
  return value.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
}

function stripHtml(text) {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatAttributes(attrs) {
  if (!attrs || typeof attrs !== 'object') return '';
  const entries = Object.entries(attrs).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
  if (!entries.length) return '';
  return entries.map(([key, value]) => `${key}=${value}`).join(', ');
}

function formatAllocations(event) {
  const allocations = event.allocations || event.allocated_resources || [];
  if (!allocations.length) return '';

  return allocations
    .map(alloc => {
      const label =
        alloc.title ||
        alloc.resource_name ||
        alloc.resource?.name ||
        'Resource';
      const start = formatTime(alloc.start_at);
      const end = formatTime(alloc.end_at);
      const range = start && end ? `${start}–${end}` : start || end || '';
      return range ? `- ${label} ${range}` : `- ${label}`;
    })
    .join('\n');
}

export function formatEventBlock(event) {
  const status = event.custom_status || event.system_stage || '';
  const startDate = formatDate(event.start_date);
  const endDate = formatDate(event.end_date);
  const dates =
    startDate && endDate && startDate !== endDate
      ? `${startDate} – ${endDate}`
      : startDate || endDate || '';

  const lines = [
    `<event id="${escapeAttr(event.id)}" name="${escapeAttr(event.name || 'Untitled')}">`,
    status ? `Status: ${status}` : null,
    dates ? `Dates: ${dates}` : null,
    event.pax != null ? `Pax: ${event.pax}` : null,
    event.current_total_price != null
      ? `Price: ${event.current_total_price}`
      : null,
    event.customer?.name
      ? `Customer: ${event.customer.name}${
          event.customer.email ? ` (${event.customer.email})` : ''
        }`
      : null,
    event.event_manager?.name ? `Manager: ${event.event_manager.name}` : null,
    event.conversation_id ? `Conversation: ${event.conversation_id}` : null,
  ].filter(Boolean);

  const allocations = formatAllocations(event);
  if (allocations) {
    lines.push('Allocations:', allocations);
  }

  if (event.description?.trim()) {
    lines.push(`Description: ${event.description.trim()}`);
  }

  const attributes = formatAttributes(event.custom_attributes);
  if (attributes) {
    lines.push(`Attributes: ${attributes}`);
  }

  lines.push('</event>');
  return lines.join('\n');
}

function formatDate(value) {
  const date = parseEventDateValue(value);
  return date ? formatDateParam(date) : '';
}

function formatContactBlock(contact) {
  const lines = [
    `<contact id="${escapeAttr(contact.id)}" name="${escapeAttr(contact.name || contact.email)}">`,
    contact.email ? `Email: ${contact.email}` : null,
    contact.phone_number ? `Phone: ${contact.phone_number}` : null,
    contact.identifier ? `Identifier: ${contact.identifier}` : null,
    '</contact>',
  ].filter(Boolean);
  return lines.join('\n');
}

function formatConversationBlock(conversation) {
  const contact = conversation.contact || conversation.raw?.contact;
  const preview = stripHtml(
    conversation.message?.content || conversation.raw?.message?.content
  );
  const lines = [
    `<conversation id="${escapeAttr(conversation.id)}" name="${escapeAttr(contact?.name || `Conversation #${conversation.id}`)}">`,
    contact?.email ? `Contact email: ${contact.email}` : null,
    conversation.inbox?.name ? `Inbox: ${conversation.inbox.name}` : null,
    preview ? `Latest message: ${preview}` : null,
    '</conversation>',
  ].filter(Boolean);
  return lines.join('\n');
}

function formatArticleBlock(article) {
  const content = stripHtml(article.content || article.raw?.content);
  const lines = [
    `<article id="${escapeAttr(article.id)}" name="${escapeAttr(article.title || article.name)}">`,
    article.portal_slug || article.portalSlug
      ? `Portal: ${article.portal_slug || article.portalSlug}`
      : null,
    article.status ? `Status: ${article.status}` : null,
    content ? `Content: ${content.slice(0, 500)}` : null,
    '</article>',
  ].filter(Boolean);
  return lines.join('\n');
}

export function buildMentionContextBlock(mention, fullRecord = null) {
  const data = fullRecord || mention.raw || mention;
  switch (mention.type) {
    case 'contact':
      return formatContactBlock(data);
    case 'conversation':
      return formatConversationBlock(data);
    case 'article':
      return formatArticleBlock(data);
    case 'event':
      return formatEventBlock(fullRecord || data);
    default:
      return '';
  }
}

function formatPreviewTag(mention) {
  const name = mention.name || '';
  const portalSlug = mention.portalSlug || mention.portal_slug;
  if (mention.type === 'article' && portalSlug) {
    return `<article id="${escapeAttr(mention.id)}" name="${escapeAttr(name)}" portal_slug="${escapeAttr(portalSlug)}"></article>`;
  }
  return `<${mention.type} id="${escapeAttr(mention.id)}" name="${escapeAttr(name)}"></${mention.type}>`;
}

export function buildAttachedContextPreview(mentions, userText = '') {
  if (!mentions?.length) return userText || '';

  const blocks = mentions.map(mention => formatPreviewTag(mention));

  const context = `<attached_context>\n${blocks.join('\n')}\n</attached_context>`;
  const trimmed = userText?.trim();
  if (!trimmed) return context;
  return `${context}\n\n${trimmed}`;
}

export function buildAttachedContext(mentions, fullRecordsByKey = {}) {
  if (!mentions?.length) return '';

  const blocks = mentions
    .map(mention => {
      const full = fullRecordsByKey[`${mention.type}:${mention.id}`];
      return buildMentionContextBlock(mention, full);
    })
    .filter(Boolean);

  if (!blocks.length) return '';
  return `<attached_context>\n${blocks.join('\n')}\n</attached_context>`;
}

export function stripAttachedContext(text) {
  if (!text || typeof text !== 'string') return text || '';

  return text
    .replace(ATTACHED_CONTEXT_RE, '')
    .replace(ATTACHED_EVENTS_RE, '')
    .replace(LEGACY_EVENT_CONTEXT_RE, '')
    .trim();
}

export function parseAttachedMentions(text) {
  if (!text || typeof text !== 'string') return [];

  const mentions = [];
  MENTION_TAG_RES.forEach(({ type, re, portalSlugIndex }) => {
    let match = re.exec(text);
    while (match) {
      const mention = {
        type,
        id: match[1],
        name: unescapeAttr(match[2]),
      };
      if (portalSlugIndex && match[portalSlugIndex]) {
        mention.portalSlug = unescapeAttr(match[portalSlugIndex]);
      }
      mentions.push(mention);
      match = re.exec(text);
    }
    re.lastIndex = 0;
  });
  return mentions;
}

export function panelKey(panel) {
  if (!panel) return '';
  if (panel.type === 'article' && panel.portalSlug) {
    return `article:${panel.portalSlug}:${panel.id}`;
  }
  return `${panel.type}:${panel.id}`;
}

export function mentionToPanel(mention) {
  if (!mention?.type || mention.id == null) return null;

  const id = String(mention.id);
  switch (mention.type) {
    case 'conversation':
      return { type: 'conversation', id };
    case 'event':
      return { type: 'event', id };
    case 'contact':
      return { type: 'contact', id };
    case 'article': {
      const portalSlug = mention.portalSlug || mention.portal_slug;
      return portalSlug
        ? { type: 'article', id, portalSlug }
        : { type: 'article', id };
    }
    default:
      return null;
  }
}

function normalizeHrefPath(href) {
  if (!href || typeof href !== 'string') return null;

  let path = href.trim();
  try {
    path = new URL(path, 'http://local.invalid').pathname;
  } catch {
    return null;
  }
  return path;
}

export function hrefToPanel(href) {
  const path = normalizeHrefPath(href);
  if (!path) return null;

  const eventMatch = path.match(
    /\/app\/accounts\/\d+\/planning\/events\/([^/]+)$/i
  );
  if (eventMatch?.[1] && eventMatch[1] !== 'new') {
    return { type: 'event', id: decodeURIComponent(eventMatch[1]) };
  }

  const conversationMatch = path.match(
    /\/app\/accounts\/\d+\/conversations\/(\d+)$/i
  );
  if (conversationMatch) {
    return { type: 'conversation', id: conversationMatch[1] };
  }

  const contactMatch = path.match(/\/app\/accounts\/\d+\/contacts\/(\d+)$/i);
  if (contactMatch) {
    return { type: 'contact', id: contactMatch[1] };
  }

  const bookingWidgetMatch = path.match(
    /\/app\/accounts\/\d+\/settings\/booking-widgets\/(\d+)$/i
  );
  if (bookingWidgetMatch) {
    return { type: 'booking_widget', id: bookingWidgetMatch[1] };
  }

  const articleMatch = path.match(
    /\/app\/accounts\/\d+\/portals\/([^/]+)\/articles\/([^/]+)$/i
  );
  if (articleMatch) {
    return {
      type: 'article',
      id: decodeURIComponent(articleMatch[2]),
      portalSlug: decodeURIComponent(articleMatch[1]),
    };
  }

  return null;
}

/**
 * Resolve entity type from a full or partial app href.
 * Used while streaming so incomplete markdown links get entity styling
 * before the full path is available for hrefToPanel.
 */
export function entityTypeFromHref(href) {
  const panel = hrefToPanel(href);
  if (panel) return panel.type;

  const path = normalizeHrefPath(href);
  if (!path || !/\/app\/accounts\//i.test(path)) return null;

  // Prefix-tolerant matches for mid-stream incomplete paths.
  if (/\/planning\/eve/i.test(path)) return 'event';
  if (/\/convers/i.test(path)) return 'conversation';
  if (/\/contact/i.test(path)) return 'contact';
  if (/\/portal/i.test(path) || /\/article/i.test(path)) return 'article';

  // Any other account path: still treat as entity so styling doesn't flash.
  return 'unknown';
}

export function mentionChipIcon(type) {
  const icons = {
    contact: 'i-lucide-user',
    conversation: 'i-lucide-message-circle',
    article: 'i-lucide-book-open',
    event: 'i-lucide-calendar-days',
  };
  return icons[type] || 'i-lucide-at-sign';
}
